import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ShoppingGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              Shopping Guide
            </span>
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4">
              BaseTao Shopping Guide: How to Shop Smarter
            </h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
              A step-by-step guide to navigating product categories, understanding shipping, and making the most of your BaseTao shopping experience.
            </p>
          </div>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Step 1: Browse Product Categories</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                BaseTao offers a wide range of product categories to explore. From <strong className="text-foreground-900">Shoes</strong> and <strong className="text-foreground-900">Hoodies/Sweaters</strong> to <strong className="text-foreground-900">Accessories</strong> and <strong className="text-foreground-900">Perfumes</strong>, each category is organized for easy browsing. You can navigate directly to any category from our Shop Categories dropdown menu or the homepage category grid.
              </p>
              <p className="text-foreground-600 leading-relaxed">
                Pro tip: Use the <strong className="text-foreground-900">BaseTao Spreadsheet</strong> to create a wishlist as you browse. Simply add product names, prices, and category labels to keep everything organized from the start.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Step 2: Compare and Select Products</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Take your time comparing products within each category. Pay attention to product details, size charts, material descriptions, and customer feedback. The key categories include:
              </p>
              <ul className="space-y-2 text-foreground-600">
                <li className="flex items-start gap-2">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-primary-500 text-sm"></i>
                  </div>
                  <span><strong className="text-foreground-900">Shoes</strong> — Sneakers, boots, slides, and athletic footwear</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-primary-500 text-sm"></i>
                  </div>
                  <span><strong className="text-foreground-900">Hoodies/Sweaters</strong> — Pullovers, zip-ups, crewnecks, and knitwear</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-primary-500 text-sm"></i>
                  </div>
                  <span><strong className="text-foreground-900">T-Shirts</strong> — Graphic tees, plain tees, long sleeves, and polos</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-primary-500 text-sm"></i>
                  </div>
                  <span><strong className="text-foreground-900">Jackets</strong> — Bombers, denim, varsity, puffers, and windbreakers</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Step 3: Track Your Orders with a Spreadsheet</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                This is where the <strong className="text-foreground-900">BaseTao Spreadsheet</strong> becomes essential. Create columns for product names, prices, order dates, warehouse status, and shipping estimates. This single dashboard gives you complete visibility into every purchase.
              </p>
              <p className="text-foreground-600 leading-relaxed">
                Learn more about setting up your spreadsheet in our <Link to="/basetao-spreadsheet/" className="text-primary-600 hover:text-primary-700 font-medium">complete BaseTao Spreadsheet guide</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Step 4: Understand Shipping Options</h2>
              <p className="text-foreground-600 leading-relaxed">
                BaseTao offers multiple shipping methods with varying costs and delivery times. Factors like package weight, destination country, and shipping carrier all affect the final price. Check our <Link to="/basetao-shipping-cost/" className="text-primary-600 hover:text-primary-700 font-medium">BaseTao Shipping Cost guide</Link> to learn how to estimate and optimize your shipping expenses.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Step 5: Monitor Warehouse Status</h2>
              <p className="text-foreground-600 leading-relaxed">
                Once your order reaches the BaseTao warehouse, it goes through several stages: arrival, quality inspection, storage, and finally shipping preparation. Our <Link to="/basetao-warehouse/" className="text-primary-600 hover:text-primary-700 font-medium">BaseTao Warehouse guide</Link> explains each step in detail so you know exactly what to expect.
              </p>
            </section>

            <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 mt-10">
              <h3 className="font-heading text-lg text-foreground-900 mb-3">Ready to Start Shopping?</h3>
              <p className="text-foreground-600 text-sm mb-4">
                Visit the main BaseTao Spreadsheet website to browse all categories, compare products, and start organizing your shopping with spreadsheets.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                Go to BaseTao Spreadsheet
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </div>
              </a>
            </div>

            <div className="text-sm text-foreground-400 mt-8 pt-6 border-t border-background-200/60 flex flex-wrap gap-4">
              <Link to="/" className="hover:text-primary-600 transition-colors">&larr; Back to Home</Link>
              <Link to="/basetao-spreadsheet/" className="hover:text-primary-600 transition-colors">BaseTao Spreadsheet Guide</Link>
              <Link to="/basetao-review/" className="hover:text-primary-600 transition-colors">BaseTao Review</Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'BaseTao Shopping Guide: How to Shop Smarter',
            description: 'A step-by-step guide to navigating product categories, understanding shipping, and making the most of your BaseTao shopping experience.',
            dateModified: '2026-08-10',
            datePublished: '2026-08-10',
            author: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
            },
            publisher: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://basetaosheet.team/shopping-guide/',
            },
          }),
        }}
      />
    </div>
  );
}